import { useCallback, useEffect, useState } from "react";
import Spinner from "../../components/ui/Spinner";
import { btnPrimary, cardCls, errMsg, inputCls } from "../../components/admin/promotionsUi";
import { gamificationService, isApprovalRequired } from "../../features/gamification/service";
import type { Quest, QuestGroup, QuestRole } from "../../features/gamification/types";
import { LoadError } from "./GamificationPanels";
import { MAX_BUDGET, MAX_CREDITS, Toggle, numStr, parseOptionalWhole, parseWhole } from "./gamificationAdminUtils";

const ROLES: QuestRole[] = ["seller", "buyer"];
const GROUP_ORDER: { key: QuestGroup; title: string; blurb: string }[] = [
  { key: "checklist", title: "Checklist quests", blurb: "Steps a new user ticks off while getting set up. Each one pays once." },
  { key: "first_time", title: "First-time rewards", blurb: "One-off rewards for a user's first time doing something." },
];

// ── Quests ───────────────────────────────────────────────────────────────────
interface QuestDraft {
  seller: string;
  buyer: string;
  roles: QuestRole[];
  enabled: boolean;
  budget: string;
}

function draftFrom(q: Quest): QuestDraft {
  return {
    seller: String(q.reward_by_role?.seller ?? q.reward_credits),
    buyer: String(q.reward_by_role?.buyer ?? q.reward_credits),
    roles: ROLES.filter((r) => q.audience_roles.includes(r)),
    enabled: q.enabled,
    budget: numStr(q.budget_credits),
  };
}

type RowState = { status: "idle" | "saving" | "ok" | "error"; message?: string };

function QuestRow({ quest, onSaved }: { quest: Quest; onSaved: (q: Quest) => void }) {
  const [draft, setDraft] = useState<QuestDraft>(() => draftFrom(quest));
  const [state, setState] = useState<RowState>({ status: "idle" });
  const unavailable = !quest.available;

  useEffect(() => { setDraft(draftFrom(quest)); }, [quest]);

  const patch = (p: Partial<QuestDraft>) => {
    setDraft((d) => ({ ...d, ...p }));
    setState({ status: "idle" });
  };

  const save = async () => {
    const s = parseWhole(draft.seller);
    const b = parseWhole(draft.buyer);
    const sellerBad = draft.roles.includes("seller") && (s === null || s > MAX_CREDITS);
    const buyerBad = draft.roles.includes("buyer") && (b === null || b > MAX_CREDITS);
    if (sellerBad || buyerBad) {
      return setState({ status: "error", message: `Credits must be whole numbers from 0 to ${MAX_CREDITS}.` });
    }
    if (draft.roles.length === 0) return setState({ status: "error", message: "Pick at least one role who can earn this." });
    const budget = parseOptionalWhole(draft.budget);
    if (budget === undefined || (budget !== null && budget > MAX_BUDGET)) {
      return setState({ status: "error", message: `Budget must be a whole number from 0 to ${MAX_BUDGET}, or blank for none.` });
    }
    setState({ status: "saving" });
    try {
      const saved = await gamificationService.updateQuest(quest.quest_id, {
        enabled: draft.enabled,
        reward_by_role: { seller: s ?? quest.reward_by_role?.seller ?? 0, buyer: b ?? quest.reward_by_role?.buyer ?? 0 },
        audience_roles: draft.roles,
        budget_credits: budget,
      });
      if (isApprovalRequired(saved)) {
        setState({ status: "ok", message: "Sent for approval (Approvals tab). Nothing changed yet." });
        return;
      }
      onSaved(saved);
      setState({ status: "ok", message: "Saved" });
    } catch (e) {
      setState({ status: "error", message: errMsg(e, "Failed to save quest") });
    }
  };

  const numCls = `${inputCls} w-24`;
  return (
    <tr className="border-t border-gray-800 align-top">
      <td className="py-3 pr-3 min-w-[9rem]">
        <p className="text-white text-sm font-medium">{quest.title || quest.name}</p>
        {quest.step_key && quest.applies_to?.length === 1 && (
          <p className="text-gray-500 text-[11px] capitalize mt-0.5">{quest.applies_to[0]} onboarding step</p>
        )}
      </td>
      <td className="py-3 pr-3 text-gray-400 text-xs min-w-[12rem]">{quest.description}</td>
      <td className="py-3 pr-3">
        {draft.roles.includes("seller") ? (
          <input type="number" min={0} max={MAX_CREDITS} aria-label={`${quest.title || quest.name} seller credits`} className={numCls} value={draft.seller} onChange={(e) => patch({ seller: e.target.value })} />
        ) : (
          <span className="text-gray-600 text-xs" title="This quest does not apply to sellers">Not for sellers</span>
        )}
      </td>
      <td className="py-3 pr-3">
        {draft.roles.includes("buyer") ? (
          <input type="number" min={0} max={MAX_CREDITS} aria-label={`${quest.title || quest.name} buyer credits`} className={numCls} value={draft.buyer} onChange={(e) => patch({ buyer: e.target.value })} />
        ) : (
          <span className="text-gray-600 text-xs" title="This quest does not apply to buyers">Not for buyers</span>
        )}
      </td>
      <td className="py-3 pr-3">
        <div className="flex flex-col gap-1">
          {/* Only the roles this reward can ever apply to: a seller-only step has no Buyer box, as it would mean nothing. */}
          {ROLES.filter((r) => !quest.applies_to || quest.applies_to.includes(r)).map((r) => (
            <label key={r} className="flex items-center gap-1.5 text-xs text-gray-300 capitalize">
              <input
                type="checkbox"
                checked={draft.roles.includes(r)}
                onChange={(e) => patch({ roles: e.target.checked ? [...draft.roles, r] : draft.roles.filter((x) => x !== r) })}
              />
              {r}
            </label>
          ))}
        </div>
      </td>
      <td className="py-3 pr-3">
        <Toggle checked={draft.enabled} onChange={(v) => patch({ enabled: v })} disabled={unavailable} label={`${quest.title || quest.name} on or off`} />
        {unavailable && <p className="text-amber-400 text-[11px] mt-1 max-w-[8rem]">Not available yet; stays off until its feature ships.</p>}
      </td>
      <td className="py-3 pr-3">
        <input type="number" min={0} max={MAX_BUDGET} aria-label={`${quest.title || quest.name} budget`} className={numCls} value={draft.budget} onChange={(e) => patch({ budget: e.target.value })} placeholder="None" />
      </td>
      <td className="py-3 pr-3 text-gray-300 text-sm whitespace-nowrap">{quest.budget_used}</td>
      <td className="py-3 min-w-[8rem]">
        <button onClick={save} disabled={state.status === "saving"} className={btnPrimary}>{state.status === "saving" ? "Saving…" : "Save"}</button>
        {state.status === "ok" && <p className="text-emerald-400 text-xs mt-1" role="status">{state.message}</p>}
        {state.status === "error" && <p className="text-red-400 text-xs mt-1 max-w-[12rem]" role="alert">{state.message}</p>}
      </td>
    </tr>
  );
}

export default function GamificationQuestsPanel({ reloadKey }: { reloadKey: number }) {
  const [loading, setLoading] = useState(true);
  const [quests, setQuests] = useState<Quest[] | null>(null);
  const [loadError, setLoadError] = useState("");

  const load = useCallback(async () => {
    try {
      setQuests(await gamificationService.listQuests());
      setLoadError("");
    } catch (e) {
      setQuests(null);
      setLoadError(errMsg(e, "Failed to load quests"));
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { void load(); }, [load, reloadKey]);

  const replace = (q: Quest) => setQuests((list) => (list ? list.map((x) => (x.quest_id === q.quest_id ? q : x)) : list));

  return (
    <section className={cardCls}>
      <h2 className="text-white font-semibold mb-1">Quests</h2>
      <p className="text-gray-500 text-xs mb-4">
        What each quest pays, to whom, and how much the quest can pay in total. Each row saves on its own.
        <span className="block text-gray-600">Example: let only sellers earn a quest by unticking Buyer, or cap a quest with a budget so it stops paying when the budget is used up.</span>
      </p>
      {loading ? (
        <div className="flex justify-center py-8"><Spinner /></div>
      ) : !quests ? (
        <LoadError message={`${loadError}. Editing is disabled so the current quests are not overwritten.`} onRetry={() => { setLoading(true); void load(); }} />
      ) : quests.length === 0 ? (
        <p className="text-gray-500 text-sm">No quests are set up yet.</p>
      ) : (
        <div className="space-y-6">
          {GROUP_ORDER.map((g) => {
            const rows = quests.filter((q) => q.group === g.key).sort((a, b) => a.order - b.order);
            if (rows.length === 0) return null;
            return (
              <div key={g.key}>
                <h3 className="text-gray-200 text-sm font-semibold">{g.title}</h3>
                <p className="text-gray-500 text-xs mb-2">{g.blurb}</p>
                <div className="overflow-x-auto">
                  <table className="w-full text-left">
                    <thead>
                      <tr className="text-xs text-gray-500">
                        <th className="pb-2 pr-3 font-medium">Name</th>
                        <th className="pb-2 pr-3 font-medium">When it pays</th>
                        <th className="pb-2 pr-3 font-medium">Credits for seller</th>
                        <th className="pb-2 pr-3 font-medium">Credits for buyer</th>
                        <th className="pb-2 pr-3 font-medium">Who can earn</th>
                        <th className="pb-2 pr-3 font-medium">On/off</th>
                        <th className="pb-2 pr-3 font-medium">Budget (credits)</th>
                        <th className="pb-2 pr-3 font-medium">Used</th>
                        <th className="pb-2 font-medium" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((q) => <QuestRow key={q.quest_id} quest={q} onSaved={replace} />)}
                    </tbody>
                  </table>
                </div>
              </div>
            );
          })}
        </div>
      )}
    </section>
  );
}
